import { ReactNode, useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';

import {
  Select,
  Typography,
  Table,
  Space,
  DatePicker,
  Button,
  Tag,
  Card,
  Statistic,
  Col,
} from 'antd';
import type { TableProps } from 'antd';

import {
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  CreditCardOutlined,
  ShoppingOutlined,
  DollarCircleOutlined,
  FilterOutlined,
} from '@ant-design/icons';

import LayoutAdmin from '@/page/LayoutAdmin';
import SalesRepository from '@/application/repositories/admin/SalesRepository';
import CompaniesRepository from '@/application/repositories/admin/CompaniesRepository';

import pix from '@/assets/img/pix-icon.svg';
import SearchFilter from '@/application/repositories/SearchFilter';
import { OperatorsEnum } from '@/application/repositories/enums/OperatorsEnum';

const { Title, Paragraph } = Typography;
const { RangePicker } = DatePicker;

interface DataType {
  key: string;
  transaction: string;
  company: string;
  page: string;
  product: string;
  amount: number;
  method: string;
  status: string;
  customer: { name: string; email: string };
  createdAt: string;
}

interface FillterType {
  companyId: null | string;
  startDate: null | string;
  endDate: null | string;
  page: number;
  perPage: number;
  paymentStatus: null | string;
}

function Status({
  status,
}: {
  status: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'refunded';
}) {
  switch (status) {
    case 'approved':
      return (
        <Tag icon={<CheckCircleOutlined />} color="success">
          Aprovado
        </Tag>
      );
    case 'pending':
      return (
        <Tag icon={<ClockCircleOutlined />} color="warning">
          Pendente
        </Tag>
      );
    case 'rejected':
      return (
        <Tag icon={<CloseCircleOutlined />} color="error">
          Rejeitado
        </Tag>
      );
    case 'cancelled':
      return (
        <Tag icon={<CloseCircleOutlined />} color="error">
          Cancelado
        </Tag>
      );
    case 'refunded':
      return (
        <Tag icon={<CheckCircleOutlined />} color="default">
          Reembolsado
        </Tag>
      );
    default:
      return (
        <Tag icon={<CheckCircleOutlined />} color="default">
          {status}
        </Tag>
      );
  }
}

function PaymentMethod({ method }: { method: 'pix' | 'credit_card' }) {
  switch (method) {
    case 'pix':
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <img src={pix} alt="" width={30} />
        </div>
      );
    case 'credit_card':
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CreditCardOutlined />
        </div>
      );
    default:
      return method;
  }
}

function DashboardCard({
  title,
  value,
  icon,
}: {
  title: string;
  value: string | number;
  icon: ReactNode;
}) {
  return (
    <Card>
      <Space direction="horizontal">
        {icon}
        <Statistic title={title} value={value} />
      </Space>
    </Card>
  );
}

const columns: TableProps<DataType>['columns'] = [
  {
    title: 'Transação',
    dataIndex: 'transaction',
    key: 'transaction',
    render: (text) => <a>#{text}</a>,
  },
  {
    title: 'Empresa',
    dataIndex: 'company',
    key: 'company',
  },
  {
    title: 'Página',
    dataIndex: 'page',
    key: 'page',
  },
  {
    title: 'Produto',
    dataIndex: 'product',
    key: 'product',
  },
  {
    title: 'Valor',
    dataIndex: 'amount',
    key: 'amount',
    render: (value) => (
      <strong>
        {Intl.NumberFormat('pt-br', {
          style: 'currency',
          currency: 'BRL',
        }).format(value)}
      </strong>
    ),
  },
  {
    title: 'Pagamento',
    dataIndex: 'method',
    key: 'method',
    render: (value) => <PaymentMethod method={value} />,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (value) => <Status status={value} />,
  },
  {
    title: 'Cliente',
    dataIndex: 'customer',
    key: 'customer',
    render: (value) => (
      <div>
        <Title level={5} style={{ margin: 0, fontSize: '13px' }}>
          {value.name}
        </Title>
        <Paragraph style={{ margin: 0, fontSize: '12px' }}>
          {value.email}
        </Paragraph>
      </div>
    ),
  },
  {
    title: 'Comprado em',
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: (value) => format(value, 'dd/MM/yyyy HH:mm:ss'),
  },
];

const paymentStatusOptions: { value: string; label: string }[] = [
  { value: 'approved', label: 'Aprovado' },
  { value: 'refunded', label: 'Rembolsado' },
  { value: 'rejected', label: 'Rejeitado' },
  { value: 'cancelled', label: 'Cancelado' },
  { value: 'pending', label: 'Pendente' },
];

const saleRepo = new SalesRepository();
const companyRepo = new CompaniesRepository();

export default function SalesPage() {
  const [sales, setSales] = useState<DataType[]>([]);
  const [companiesOptions, setCompaniesOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<FillterType>({
    companyId: null,
    startDate: null,
    endDate: null,
    page: 1,
    perPage: 10,
    paymentStatus: null,
  });
  const [pagination, setPagination] = useState({
    perPage: 10,
    total: 10,
  });
  const [invoincing, setInvoicing] = useState({
    totalAmount: 0,
    totalCount: 0,
  });

  const handleGetCompanies = async () => {
    try {
      const companies = await companyRepo.getAll();

      setCompaniesOptions(() =>
        companies.map(({ name, document, id }) => ({
          value: id,
          label: `${name} ${document ? '- ' + document : ''}`,
        }))
      );
    } catch (error) {
    } finally {
    }
  };

  const handleSearchSales = useCallback(async () => {
    try {
      setLoading(true);

      const searchFilter = new SearchFilter();

      if (filter.companyId) {
        searchFilter.addFilter(
          'company_id',
          filter.companyId,
          OperatorsEnum.Equal
        );
      }

      if (filter.startDate) {
        searchFilter.addRangeDate(
          filter.startDate as string,
          filter.endDate as string
        );
      }

      if (filter.paymentStatus) {
        searchFilter.addFilter(
          'payment_status',
          filter.paymentStatus,
          OperatorsEnum.Equal
        );
      }

      searchFilter.addPagination(filter.page, filter.perPage);

      const { data, perPage, total } = await saleRepo.search(searchFilter);
      const { totalAmount, totalCount } = await saleRepo.getInvoincing(
        searchFilter
      );

      setPagination({ perPage, total });
      setInvoicing({ totalAmount, totalCount });

      setSales(() =>
        data.map(({ payment, page, company, product, customer }) => ({
          key: payment.transaction,
          transaction: payment.transaction,
          company: company,
          page: page,
          product: product.name,
          amount: payment.amount,
          method: payment.method,
          status: payment.status,
          customer: { name: customer.name, email: customer.email },
          createdAt: payment.createdAt,
        }))
      );
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    handleSearchSales();
  }, [filter, handleSearchSales]);

  useEffect(() => {
    handleSearchSales();
    handleGetCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterOption = (
    input: string,
    option?: { label: string; value: string }
  ) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

  const handlePagination = (page: number) => {
    //This should be called only when pagination changes
    setFilter((state) => ({ ...state, page }));
  };

  return (
    <LayoutAdmin>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Title level={2}>Vendas</Title>
        <Card
          title={
            <>
              <FilterOutlined /> Filtros
            </>
          }
        >
          <Space size="large" wrap={true} style={{ width: '100%' }}>
            <Col sm={28}>
              <Select
                showSearch
                style={{ width: '100%' }}
                allowClear
                placeholder="Filtrar por Empresa"
                optionFilterProp="children"
                filterOption={handleFilterOption}
                options={companiesOptions}
                onChange={(value) =>
                  setFilter((state) => ({
                    ...state,
                    companyId: value,
                    page: 1,
                  }))
                }
              />
            </Col>
            <Col xs={28}>
              <Select
                showSearch
                style={{ width: '100%' }}
                allowClear
                placeholder="Status do Pagamento"
                optionFilterProp="children"
                filterOption={handleFilterOption}
                options={paymentStatusOptions}
                onChange={(value) =>
                  setFilter((state) => ({
                    ...state,
                    paymentStatus: value,
                    page: 1,
                  }))
                }
              />
            </Col>
            <Col xs={28}>
              <RangePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                onChange={(date) => {
                  let startDate: string | null = null;
                  let endDate: string | null = null;

                  if (date && date[0] && date[1]) {
                    startDate = date[0].format('YYYY-MM-DD');
                    endDate = date[1].format('YYYY-MM-DD');
                  }

                  setFilter((state) => ({
                    ...state,
                    startDate,
                    endDate,
                    page: 1,
                  }));
                }}
              />
            </Col>
            <Col xs={28}>
              <Button
                style={{ width: '100%' }}
                type="primary"
                shape="round"
                icon={<DownloadOutlined />}
                onClick={() => {
                  const searchFilter = new SearchFilter();

                  if (filter.companyId) {
                    searchFilter.addFilter(
                      'company_id',
                      filter.companyId,
                      OperatorsEnum.Equal
                    );
                  }

                  if (filter.startDate) {
                    searchFilter.addRangeDate(
                      filter.startDate as string,
                      filter.endDate as string
                    );
                  }

                  if (filter.paymentStatus) {
                    searchFilter.addFilter(
                      'payment_status',
                      filter.paymentStatus,
                      OperatorsEnum.Equal
                    );
                  }

                  saleRepo.exportCsv(searchFilter);
                }}
              >
                CSV
              </Button>
            </Col>
          </Space>
        </Card>

        <Space wrap={true}>
          <DashboardCard
            icon={
              <DollarCircleOutlined
                style={{
                  color: 'green',
                  backgroundColor: 'rgba(0,255,0,0.25)',
                  borderRadius: 20,
                  fontSize: 24,
                  padding: 8,
                }}
              />
            }
            title="Total"
            value={Intl.NumberFormat('pt-br', {
              style: 'currency',
              currency: 'BRL',
            }).format(invoincing.totalAmount)}
          />

          <DashboardCard
            icon={
              <ShoppingOutlined
                style={{
                  color: 'blue',
                  backgroundColor: 'rgba(0,0,255,0.25)',
                  borderRadius: 20,
                  fontSize: 24,
                  padding: 8,
                }}
              />
            }
            title="Total vendas"
            value={invoincing.totalCount}
          />
        </Space>

        <Card>
          <Table
            columns={columns}
            dataSource={sales}
            loading={loading}
            scroll={{ x: 1000 }}
            pagination={{
              total: pagination.total,
              pageSize: pagination.perPage,
              onChange: handlePagination,
              totalBoundaryShowSizeChanger: 20,
              onShowSizeChange: (value, perPage) =>
                setFilter({ ...filter, perPage }),
            }}
          />
        </Card>
      </Space>
    </LayoutAdmin>
  );
}
